import React from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@roger/ui-components';
import { findGridRoot, focusGrid } from '../dom';
import { ChecklistList, type ChecklistVariant } from './ChecklistList';
import type { Checklist } from './useChecklist';

/**
 * A checklist opened from `trigger`, which must take `checklist.triggerRef`.
 * The list takes focus on opening; closing hands the keys back to the grid
 * the trigger sits in, so the arrow keys carry on from there.
 */
export function ChecklistPopover({
  checklist,
  trigger,
  contentClassName,
  sideOffset,
  ...list
}: {
  checklist: Checklist;
  trigger: React.ReactElement;
  contentClassName: string;
  sideOffset: number;
  ariaLabel: string;
  variant: ChecklistVariant;
}) {
  return (
    <Popover open={checklist.open} onOpenChange={checklist.onOpenChange}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        align='start'
        sideOffset={sideOffset}
        className={contentClassName}
        onOpenAutoFocus={e => {
          e.preventDefault();
          checklist.listRef.current?.focus();
        }}
        onCloseAutoFocus={e => {
          // In a grid the list has usually handed the keys on already (see
          // useChecklist's close); only pick them up if they were left with
          // this checklist, never pull them back from where a Tab sent them.
          const grid = findGridRoot(checklist.triggerRef.current);
          if (!grid) return;
          e.preventDefault();
          if (checklist.keysAreHere()) focusGrid(grid);
        }}
      >
        <ChecklistList checklist={checklist} {...list} />
      </PopoverContent>
    </Popover>
  );
}
